import HTTP_REQUEST from './httpRequest';
import settings from '../utils/settings';
import { SERVICES } from './constants';
import { HTTPS_METHOD, STATUS } from '../utils/constant';
import { v4 as uuid } from "uuid";

const HCServices = (function () {

  // -------------------------
  // COMMON API WRAPPER
  // -------------------------
  function callAPI(method, url, body = null) {
    return new Promise((resolve, reject) => {

      const cbSuccess = (response) => resolve(response);
      const cbError = (error) => reject(error);

      let headerForRequest = {}
      if (method !== HTTPS_METHOD.GET) {
        headerForRequest.xhrFields = { withCredentials: true };
        headerForRequest.body = body;
        headerForRequest.headers = {
          "Content-Type": "application/json"
        }
      }

      if (!HTTP_REQUEST[method]) {
        reject(new Error("Invalid Method"));
        return;
      }

      HTTP_REQUEST[method](url, headerForRequest, cbSuccess, cbError);
    });
  }

  // -------------------------
  // QUERY STRING BUILDER
  // -------------------------
  const serialize = (obj) =>
    Object.keys(obj)
      .map((key) => `${key}=${encodeURIComponent(obj[key])}`)
      .join("&");

  // -------------------------
  // API METHODS
  // -------------------------

  async function getCountryDetails() {
    try {
      const url = settings.getCoreUrl(`api/v1/config/supported_countries`, SERVICES.PROVISION);
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE };
    }
  }

  async function getCountryOptions(locale) {
    try {
      const url = settings.getCoreUrl(`support/country_options?locale=${locale}`, SERVICES.ENLIGHTEN);
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getStatesForCountry(countryCode, locale) {
    try {
      const url = settings.getCoreUrl(
        `support/country_states?country=${countryCode}&locale=${locale}`,
        SERVICES.ENLIGHTEN
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function addressSearchApi(address) {
    try {
      const url = settings.getCoreUrl(`get_prediction?${serialize(address)}`, SERVICES.PROVISION);
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function addressDetailsApi(address) {
    try {
      const url = settings.getCoreUrl(`get_address?${serialize(address)}`, SERVICES.PROVISION);
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function postSiteDetailsValidate(siteData, isExistingUser) {
    try {
      const url = settings.getCoreUrl(
        `evse/validate/address?isExistingUser=${isExistingUser}`,
        SERVICES.PROVISION
      );
      const result = await callAPI(HTTPS_METHOD.POST, url, siteData);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE, error };
    }
  }

  async function getHVACModeDetails(siteId) {
    try {
      const url = settings.getCoreUrl(
        `service/batteryConfig/api/v1/profile/${siteId}?source=enho`,
        SERVICES.ENLIGHTEN
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getSiteSettings(siteId) {
    try {
      const url = settings.getCoreUrl(
        `service/batteryConfig/api/v1/siteSettings/${siteId}`,
        SERVICES.ENLIGHTEN
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getHVACDetails(siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/hvac?device-id=${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getCustomSchedules(siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/schedules/custom?device-id=${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

   async function setCustomSchedules(payload,siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/schedules/custom?device-id=${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.POST, url,payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getAirDeviceDetails(siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/air?device-id=${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function setAirDeviceSettings(payload, siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/air/${deviceId}/settings`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.PUT, url, payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getAirDevices(siteId, isRefresh = false) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/air-devices?refresh=${isRefresh}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function getAirDeviceById(siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/air-devices?device-id=${deviceId}&refresh=false`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function setHVACDetails(siteId, deviceId, payload) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/hvac/${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.PUT, url, payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function validateSerialNumber(serialNum) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/validate?device_type=IQ_AIR&serial_num=${serialNum}`,
        SERVICES.SITEDEVICE
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE, error };
    }
  }

  async function createSite(payload) {
    try {
      const url = settings.getCoreUrl(`api/v1/systems`, SERVICES.SITEDEVICE);
      const result = await callAPI(HTTPS_METHOD.POST, url, payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE, error };
    }
  }

  async function setEnergyMode(siteId,payload) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/energy-modes`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.PUT, url, payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function linkDevice(siteId, serialNumber) {
    
    const url = settings.getCoreUrl(`api/v1/devices/${siteId}/air`, SERVICES.HOMECONNECT);
    const body = { "serial-number": serialNumber };

    try {
      const result = await callAPI(HTTPS_METHOD.POST, url, body);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return {
        status: STATUS.FAILURE,
        httpStatus: error?.status,
        errorCode: error?.responseJSON?.error?.code,
        errorStatus: error?.responseJSON?.error?.status,
        errorMessage: error?.responseJSON?.error?.message,
        rawError: error
      };
    }
  }

  async function getConnectedDevices(siteId) {
    try {
      const url = settings.getCoreUrl(`api/v1/devices/${siteId}/device-details`, SERVICES.HOMECONNECT);
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE };
    }
  }

  async function addNewHvac(siteId) {
    try {
      const url = settings.getCoreUrl(`api/v1/devices/${siteId}/hvac`, SERVICES.HOMECONNECT);
      const result = await callAPI(HTTPS_METHOD.POST, url, {});
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE, error };
    }
  }

   async function linkHvac(payload, siteId) {
    try {
      const url = settings.getCoreUrl(`api/v1/devices/${siteId}/link-hvac`, SERVICES.HOMECONNECT);
      const result = await callAPI(HTTPS_METHOD.PUT, url, payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function deleteHvac(siteId, hvacId) {
    try {
      const url = settings.getCoreUrl(`api/v1/devices/${siteId}/hvac/${hvacId}`, SERVICES.HOMECONNECT);
      const result = await callAPI(HTTPS_METHOD.DELETE, url, {});
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE, error };
    }
  }

  async function getCustomScheduleScene(siteId, deviceId) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/presets?device-id=${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.GET, url);
      return { ...result, status: STATUS.SUCCESS };
    } catch {
      return { status: STATUS.FAILURE };
    }
  }

  async function updateCustomScheduleScene(siteId, deviceId, presetId, payload) {
    try {
      const url = settings.getCoreUrl(
        `api/v1/devices/${siteId}/presets/${presetId}?device-id=${deviceId}`,
        SERVICES.HOMECONNECT
      );
      const result = await callAPI(HTTPS_METHOD.PUT, url, payload);
      return { ...result, status: STATUS.SUCCESS };
    } catch (error) {
      return { status: STATUS.FAILURE, error };
    }
  }

  // -------------------------
  // EXPORT
  // -------------------------
  return {
    getCountryDetails,
    getCountryOptions,
    getStatesForCountry,
    addressSearchApi,
    addressDetailsApi,
    postSiteDetailsValidate,
    getHVACModeDetails,
    getHVACDetails,
    getAirDeviceDetails,
    setAirDeviceSettings,
    getAirDevices,
    getAirDeviceById,
    setHVACDetails,
    validateSerialNumber,
    createSite,
    linkDevice,
    getConnectedDevices,
    addNewHvac,
    linkHvac,
    deleteHvac,
    setEnergyMode,
    getSiteSettings,
    getCustomScheduleScene,
    updateCustomScheduleScene,
    getCustomSchedules,
    setCustomSchedules
  };
})();

export default HCServices;
